import { type PropsWithChildren, type ReactNode, type FunctionComponent } from 'react'
import { PageHeader } from '@retailer-platform/shared-components/src/tds'
import { useIsRetailerSelectRequired, useStoreConfigData } from './ScopeSelection.hooks'
import { StoreConfigScopeWrapper } from './StoreConfigScopeWrapper'
import { ScopeSelector, type ScopeSelectorProps } from './ScopeSelector'
import { ViewType } from './ScopeSelection.types'
import { BannerSelectRequired } from './BannerSelectRequired'

interface ScopeSelectionProps extends ScopeSelectorProps {
  storeConfigSelectionOptional?: boolean
}

export interface ScopeSelectionGateProps {
  /**
   * Props passed to the scope selector
   * and used to determine if retailer selection is required
   */
  scopeSelector?: ScopeSelectionProps
  /**
   * Page Header title displayed when retailer selection is required
   * Has no effect if `includePageHeader` is false
   */
  fallbackTitle?: string
  /**
   * Custom content displayed when retailer selection is required
   */
  fallbackContent?: ReactNode
  /**
   * Controls whether a default PageHeader is rendered,
   * when retailer selection is required. Defaults to true
   */
  includePageHeader?: boolean
  /**
   * Controls whether a BannerSelectRequired component is rendered,
   * when retailer selection is required. Defaults to true
   */
  includeBannerSelectImage?: boolean
  children: ReactNode
}

/**
 * This component conditionally renders children, based on the provided scopeSelection props.
 * This should only be used as a wrapper for tools requiring a retailer/scope selection to function.
 */
export const ScopeSelectionGate: FunctionComponent<PropsWithChildren<ScopeSelectionGateProps>> = ({
  children,
  fallbackContent,
  fallbackTitle = '',
  includePageHeader = true,
  includeBannerSelectImage = true,
  scopeSelector,
}) => {
  const { storeConfigSelectionOptional, ...scopeSelectorProps } = scopeSelector ?? {}
  const { selectionType, retailerSelectionOptional, hiddenScopePicker } = scopeSelectorProps ?? {}

  const { storeConfigId, storeConfigIds, storeConfigurations } = useStoreConfigData()
  const storeConfigs = storeConfigurations?.data?.storeConfigurationsByRetailerIds

  const isRetailerSelectionRequired = useIsRetailerSelectRequired({
    selectionType,
    retailerSelectionOptional,
    hiddenScopePicker,
    storeConfigSelectionOptional,
  })

  if (!isRetailerSelectionRequired) {
    if (
      selectionType === ViewType.RETAILER_AND_STORE_CONFIG ||
      selectionType === ViewType.STORE_CONFIG
    ) {
      return (
        <StoreConfigScopeWrapper
          storeConfig={storeConfigs?.find(e => e.id === String(storeConfigId))}
          storeConfigs={storeConfigs?.filter(e => storeConfigIds.includes(e.id))}
        >
          {children}
        </StoreConfigScopeWrapper>
      )
    }
    return <>{children}</>
  }

  return (
    <>
      {includePageHeader && (
        <PageHeader
          title={{ text: fallbackTitle }}
          filters={[
            {
              key: 'retailer-scope-selector',
              filter: <ScopeSelector {...scopeSelectorProps} margin="0" />,
            },
          ]}
        />
      )}
      {fallbackContent}
      {includeBannerSelectImage && <BannerSelectRequired selectionType={selectionType} />}
    </>
  )
}
